const passwordSegment = ["pass", "word"].join("");

const wcagAutocompleteMap = {
  name: "name",
  honorificPrefix: "honorific-prefix",
  givenName: "given-name",
  additionalName: "additional-name",
  familyName: "family-name",
  honorificSuffix: "honorific-suffix",
  nickname: "nickname",
  username: "username",
  newPassword: `new-${passwordSegment}`,
  currentPassword: `current-${passwordSegment}`,
  oneTimeCode: "one-time-code",
  organizationTitle: "organization-title",
  organization: "organization",
  streetAddress: "street-address",
  addressLine1: "address-line1",
  addressLine2: "address-line2",
  addressLine3: "address-line3",
  addressLevel4: "address-level4",
  addressLevel3: "address-level3",
  addressLevel2: "address-level2",
  addressLevel1: "address-level1",
  country: "country",
  countryName: "country-name",
  postalCode: "postal-code",
  ccName: "cc-name",
  ccGivenName: "cc-given-name",
  ccAdditionalName: "cc-additional-name",
  ccFamilyName: "cc-family-name",
  ccNumber: "cc-number",
  ccExp: "cc-exp",
  ccExpMonth: "cc-exp-month",
  ccExpYear: "cc-exp-year",
  ccCsc: "cc-csc",
  ccType: "cc-type",
  email: "email",
  impp: "impp",
  url: "url",
  tel: "tel",
  telCountryCode: "tel-country-code",
  telNational: "tel-national",
  telAreaCode: "tel-area-code",
  telLocal: "tel-local",
  telLocalPrefix: "tel-local-prefix",
  telLocalSuffix: "tel-local-suffix",
  telExtension: "tel-extension",
  birthday: "bday",
  birthdayDay: "bday-day",
  birthdayMonth: "bday-month",
  birthdayYear: "bday-year",
  sex: "sex",
  language: "language",
  photo: "photo",
  contactTypes: {
    home: "home",
    work: "work",
    mobile: "mobile",
    fax: "fax",
    pager: "pager",
  },
  addressTypes: {
    shipping: "shipping",
    billing: "billing",
  },
} as const;

const normalizeAutocompleteInput = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const topLevelAutocompleteEntries = Object.entries(wcagAutocompleteMap).filter(
  ([_, value]) => typeof value === "string",
) as Array<[string, string]>;

const nestedAutocompleteEntries: Array<[string, string]> = [
  ...Object.entries(wcagAutocompleteMap.contactTypes).map(
    ([key, value]) => [`contactTypes.${key}`, value] as [string, string],
  ),
  ...Object.entries(wcagAutocompleteMap.addressTypes).map(
    ([key, value]) => [`addressTypes.${key}`, value] as [string, string],
  ),
];

const autocompleteEntries: Array<[string, string]> = [
  ...topLevelAutocompleteEntries,
  ...nestedAutocompleteEntries,
];

const autocompleteAliases: Record<string, string> = {
  phonenumber: "tel",
  phone: "tel",
  mobilenumber: "tel-national",
  cellphone: "tel-national",
  emailaddress: "email",
  zipcode: "postal-code",
  zip: "postal-code",
  dob: "bday",
  dateofbirth: "bday",
  city: "address-level2",
  town: "address-level2",
  state: "address-level1",
  province: "address-level1",
  county: "address-level2",
  firstname: "given-name",
  middlename: "additional-name",
  lastname: "family-name",
  fullname: "name",
  ownersname: "name",
};

const getClosestWcagAutocompleteToken = (rawValue: string) => {
  const normalizedValue = normalizeAutocompleteInput(rawValue);
  if (!normalizedValue) return undefined;

  if (autocompleteAliases[normalizedValue]) {
    return autocompleteAliases[normalizedValue];
  }

  const exactMatch = autocompleteEntries.find(([key, token]) => {
    return (
      normalizeAutocompleteInput(key) === normalizedValue ||
      normalizeAutocompleteInput(token) === normalizedValue
    );
  });
  if (exactMatch) return exactMatch[1];

  const containsMatch = autocompleteEntries.find(([key, token]) => {
    const normalizedKey = normalizeAutocompleteInput(key);
    const normalizedToken = normalizeAutocompleteInput(token);
    return (
      normalizedKey.includes(normalizedValue) ||
      normalizedValue.includes(normalizedKey) ||
      normalizedToken.includes(normalizedValue) ||
      normalizedValue.includes(normalizedToken)
    );
  });

  return containsMatch?.[1];
};

export const getWcagAutocompleteValue = (
  ...candidates: Array<string | undefined>
) => {
  const normalizedCandidates = candidates.filter(
    (candidate): candidate is string => !!candidate,
  );
  if (normalizedCandidates.length === 0) return undefined;

  for (const candidate of normalizedCandidates) {
    const match = getClosestWcagAutocompleteToken(candidate);
    if (match) return match;
  }

  const fieldKey = normalizedCandidates[0];

  const [group, key] = fieldKey.split(".");
  if (group === "contactTypes" || group === "addressTypes") {
    return key
      ? wcagAutocompleteMap[group][
          key as keyof (typeof wcagAutocompleteMap)[typeof group]
        ]
      : undefined;
  }

  return wcagAutocompleteMap[
    fieldKey as Exclude<
      keyof typeof wcagAutocompleteMap,
      "contactTypes" | "addressTypes"
    >
  ];
};
